import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import xaleLogo from "@/assets/Logo.png";
import { useAppDispatch, useAppSelector } from "@/stores/hook";
import { refetchCurrentLead } from "@/features/leads/api/useCurrentLead";
import { closeVoiceCall, setLanguage, setVoiceCallRequest } from "../stores/aiChatSlice";
import { useLiveVoice, type CallStatus } from "../voice/useLiveVoice";
import type { TelecallerStartOptions } from "../voice/telecaller.types";
import SiriOrb from "./voice/SiriOrb";
import RecordingCard from "./voice/RecordingCard";
import ChartCard from "./voice/ChartCard";
import TelecallerSetup from "./voice/TelecallerSetup";
import LeadPicker from "./voice/LeadPicker";
import TelecallerCard from "./voice/TelecallerCard";
import OutcomeCard from "./voice/OutcomeCard";
import { useVoiceCallStyles } from "./voice/voiceCallStyles";
import { TEMPO_MAX, TEMPO_MIN, TEMPO_STEP, formatTempo } from "../voice/tempo";

// ---------------------------------------------------------------------------
// VoiceCallOverlay — the full-screen "talk to Xale AI" call.
//
// Apple-grade dark glass: a deep Xale-green ground with drifting aurora and
// film grain, a liquid Siri orb that reacts to whoever is speaking, an edge
// light that breathes with the audio, Apple-style captions that fade in word
// by word, frosted controls, and the transcript tucked into a pull-up sheet.
//
// The call machinery is untouched — every behaviour still comes from
// useLiveVoice (barge-in, speech gate, spoken stop / continue, tool activity).
// Malayalam is the default language. Styles are injected, never Tailwind, so
// the overlay is safe to portal and never inherits the app theme.
//
// TELECALLER MODE (opened from a lead's header): a setup sheet first
// (objective, language, voice, caller name), then the same call with the AI
// speaking FIRST as the consultancy's telecaller. The lead card shows who is
// on the line; the outcome card shows the note / follow-up the AI wrote. The
// keyboard becomes a whisper channel to the AI (the lead never sees it).
// ---------------------------------------------------------------------------

type Lang = "ml" | "en";

const COPY: Record<Lang, Record<CallStatus, string>> = {
  ml: {
    idle: "",
    connecting: "കണക്റ്റ് ചെയ്യുന്നു…",
    listening: "കേൾക്കുന്നു… പറഞ്ഞോളൂ",
    thinking: "ആലോചിക്കുന്നു…",
    speaking: "പറയുന്നു…",
    ended: "കോൾ അവസാനിച്ചു",
    error: "പ്രശ്നമുണ്ടായി",
  },
  en: {
    idle: "",
    connecting: "Connecting…",
    listening: "Listening… go ahead",
    thinking: "Thinking…",
    speaking: "Speaking…",
    ended: "Call ended",
    error: "Something went wrong",
  },
};

const T = {
  ml: {
    live: "ലൈവ് വോയ്സ്",
    transcript: "സംഭാഷണം",
    you: "നിങ്ങൾ",
    ai: "Xale AI",
    callAgain: "വീണ്ടും വിളിക്കുക",
    mute: "മ്യൂട്ട്",
    unmute: "അൺമ്യൂട്ട്",
    type: "ടൈപ്പ്",
    end: "അവസാനിപ്പിക്കുക",
    micOff: "മൈക്ക് ഓഫ്",
    placeholder: "ടൈപ്പ് ചെയ്യൂ…",
    send: "അയയ്ക്കുക",
    opening: "ലീഡുകൾ, ടീം, റിപ്പോർട്ട് — എന്തും ചോദിക്കാം",
    close: "അടയ്ക്കുക",
    telecaller: "AI Telecaller",
    lead: "ലീഡ്",
    whisper: "നിങ്ങൾ (whisper)",
    whisperPlaceholder: "AI-യോട് രഹസ്യമായി പറയൂ… (ഉദാ: wrap up ചെയ്യൂ)",
    telecallerOpening: "ഫോൺ speaker-ൽ വെക്കുക — AI ആദ്യം സംസാരിക്കും",
    practiceOpening: "പ്രാക്ടീസ് — നിങ്ങളാണ് ലീഡ്; ലീഡ് പറയുന്നത് പോലെ മറുപടി പറയൂ",
    practice: "പ്രാക്ടീസ്",
    callAnother: "വീണ്ടും വിളിക്കുക",
    tempo: "AI speed",
    tempoHint: "AI സംസാരിക്കുന്ന speed — pitch മാറില്ല; ഈ pace-ന് ഓർത്തുവെക്കും",
    modeAssistant: "അസിസ്റ്റന്റ്",
    modeTelecaller: "ടെലികോളർ",
  },
  en: {
    live: "Live voice",
    transcript: "Transcript",
    you: "You",
    ai: "Xale AI",
    callAgain: "Call again",
    mute: "Mute",
    unmute: "Unmute",
    type: "Type",
    end: "End call",
    micOff: "Mic off",
    placeholder: "Type instead…",
    send: "Send",
    opening: "Ask about your leads, your team, any report",
    close: "Close",
    telecaller: "AI Telecaller",
    lead: "Lead",
    whisper: "You (whisper)",
    whisperPlaceholder: "Whisper to the AI… (e.g. wrap up now)",
    telecallerOpening: "Put the phone on speaker — the AI speaks first",
    practiceOpening: "Practice — you are the lead; answer the way the lead would",
    practice: "Practice",
    callAnother: "Call again",
    tempo: "AI speed",
    tempoHint: "How fast the AI talks — pitch unchanged; remembered for this pace",
    modeAssistant: "Assistant",
    modeTelecaller: "Telecaller",
  },
} as const;

export default function VoiceCallOverlay() {
  useVoiceCallStyles();
  const dispatch = useAppDispatch();
  const open = useAppSelector((s) => s.aiChat.voiceCallOpen);
  const request = useAppSelector((s) => s.aiChat.voiceCallRequest);
  const language = useAppSelector((s) => s.aiChat.language) as Lang;
  const isTelecallerRequest = request.mode === "telecaller";
  const {
    state, start, stop, toggleMute, sendText, setTempo, levels,
    dismissMedia, dismissChart, registerMediaElement, onMediaPlay, onMediaPause, onMediaEnded,
  } = useLiveVoice();

  const [typing, setTyping] = useState(false);
  const [draft, setDraft] = useState("");
  const [showTranscript, setShowTranscript] = useState(false);
  const [viewport, setViewport] = useState(() => ({
    w: typeof window === "undefined" ? 1280 : window.innerWidth,
    h: typeof window === "undefined" ? 800 : window.innerHeight,
  }));
  const [elapsed, setElapsed] = useState(0);
  const [connectedAt, setConnectedAt] = useState<number | null>(null);

  const edgeRef = useRef<HTMLDivElement | null>(null);
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const startedRef = useRef(false);
  /** Telecaller: the sheet is showing (before the line opens, or after a refusal). */
  const [setupOpen, setSetupOpen] = useState(false);
  /** Telecaller: no lead chosen yet — the picker is showing. */
  const [pickerOpen, setPickerOpen] = useState(false);
  /** The request the overlay has already acted on (mode + lead) — a changed
   *  request while open (the mode switch, a picked lead) is acted on once. */
  const handledRequestRef = useRef<string | null>(null);
  /** Telecaller: the last options used — language switch / retry reuse them. */
  const telecallerOptsRef = useRef<Extract<TelecallerStartOptions, { mode: "telecaller" }> | null>(null);
  const [refusal, setRefusal] = useState<{ code: string; message: string } | null>(null);
  const outcomeSeenRef = useRef<number | null>(null);

  const t = T[language];
  const status = state.status;
  const live = status === "listening" || status === "speaking" || status === "thinking";
  const canTalk = live;

  // --- call lifecycle ------------------------------------------------------
  // One effect decides what the open overlay does from the REQUEST in the
  // slice: assistant → start the call; telecaller with a lead → the setup
  // sheet; telecaller without a lead → the picker. The same effect handles a
  // request that changes while open (the header switch, a picked lead): the
  // running call is stopped and the new request is acted on once.
  useEffect(() => {
    if (!open) {
      if (startedRef.current) {
        startedRef.current = false;
        handledRequestRef.current = null;
        setConnectedAt(null);
        setShowTranscript(false);
        setTyping(false);
        setElapsed(0);
        setSetupOpen(false);
        setPickerOpen(false);
        setRefusal(null);
        telecallerOptsRef.current = null;
        outcomeSeenRef.current = null;
        stop("closed");
      }
      return;
    }
    const key = request.mode === "telecaller" ? `telecaller:${request.leadId ?? ""}` : "assistant";
    if (handledRequestRef.current === key) return;
    const switching = startedRef.current;
    handledRequestRef.current = key;
    startedRef.current = true;
    if (switching) {
      stop("switch");
      setConnectedAt(null);
      setElapsed(0);
      setShowTranscript(false);
      setTyping(false);
    }
    setRefusal(null);
    if (request.mode === "telecaller") {
      telecallerOptsRef.current = null;
      if (request.leadId) {
        setPickerOpen(false);
        setSetupOpen(true);
      } else {
        setSetupOpen(false);
        setPickerOpen(true);
      }
      return;
    }
    setPickerOpen(false);
    setSetupOpen(false);
    void start(language);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, request]);

  /** Header switch: Assistant ⇄ Telecaller. Telecaller opens the picker unless a lead is already chosen. */
  const switchMode = useCallback(
    (next: "assistant" | "telecaller") => {
      if (next === request.mode) return;
      dispatch(setVoiceCallRequest(next === "telecaller" ? { mode: "telecaller" } : { mode: "assistant" }));
    },
    [dispatch, request.mode],
  );

  const pickLead = useCallback(
    (lead: { id: number; name: string }) => {
      dispatch(setVoiceCallRequest({ mode: "telecaller", leadId: lead.id, leadName: lead.name }));
    },
    [dispatch],
  );

  // Telecaller: a refusal the launcher may override (outside hours / do-not-
  // call) reopens the sheet with the reason; other errors stay as errors.
  useEffect(() => {
    if (!isTelecallerRequest || state.status !== "error") return;
    if (state.errorCode === "OUTSIDE_CALLING_HOURS" || state.errorCode === "DO_NOT_CALL") {
      setRefusal({ code: state.errorCode, message: state.error ?? "" });
      setSetupOpen(true);
    }
  }, [isTelecallerRequest, state.status, state.errorCode, state.error]);

  // Telecaller: the AI wrote a note / follow-up → the open lead view must show it.
  useEffect(() => {
    const id = state.outcome?.noteId ?? null;
    if (id == null || outcomeSeenRef.current === id) return;
    outcomeSeenRef.current = id;
    void refetchCurrentLead();
  }, [state.outcome]);

  const startTelecaller = useCallback(
    (opts: Extract<TelecallerStartOptions, { mode: "telecaller" }>) => {
      if (request.mode !== "telecaller" || !request.leadId) return;
      const full = { ...opts, leadId: request.leadId };
      telecallerOptsRef.current = full;
      setSetupOpen(false);
      setRefusal(null);
      setConnectedAt(null);
      setElapsed(0);
      void start(language, full);
    },
    [request, language, start],
  );

  // --- call timer: starts when the line goes live, freezes when it ends -----
  useEffect(() => {
    if (!open || !live) return;
    if (connectedAt == null) {
      setConnectedAt(Date.now());
      return;
    }
    const tick = () => setElapsed(Math.floor((Date.now() - connectedAt) / 1000));
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [open, live, connectedAt]);

  // --- viewport ------------------------------------------------------------
  useEffect(() => {
    if (!open) return;
    const measure = () => setViewport({ w: window.innerWidth, h: window.innerHeight });
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [open]);

  // --- Esc ends the call ---------------------------------------------------
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (showTranscript) setShowTranscript(false);
      else if (typing) setTyping(false);
      else dispatch(closeVoiceCall());
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, showTranscript, typing, dispatch]);

  // Keep the transcript sheet pinned to the newest line.
  useEffect(() => {
    if (!showTranscript) return;
    const el = bodyRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [showTranscript, state.transcript.length]);

  /** Edge light follows the audio — written straight to the DOM, no re-render. */
  const handleEnergy = useCallback((energy: number) => {
    const el = edgeRef.current;
    if (el) el.style.opacity = (0.14 + Math.min(1, energy) * 0.8).toFixed(3);
  }, []);

  const lastUser = useMemo(
    () => [...state.transcript].reverse().find((l) => l.role === "user")?.text ?? "",
    [state.transcript],
  );
  const lastAI = useMemo(
    () => [...state.transcript].reverse().find((l) => l.role === "assistant")?.text ?? "",
    [state.transcript],
  );

  if (!open) return null;

  // The orb gets whatever height the chrome does not need. ORB_BLEED is the
  // share of the canvas that is transparent bloom, reclaimed by negative margin.
  const ORB_BLEED = 0.26;
  const reserve =
    84 + 46 + 124 + 108 + (state.media ? 190 : 0) + (state.chart ? 340 : 0) + (typing ? 62 : 0) +
    (state.telecaller && !state.outcome ? 150 : 0) + (state.outcome ? 210 : 0) + (setupOpen ? 460 : 0) + (pickerOpen ? 420 : 0);
  const orbSize = Math.round(
    Math.max(170, Math.min(480, Math.min(viewport.w * 0.82, (viewport.h - reserve) / (1 - ORB_BLEED)))),
  );

  const youText = state.liveUser || lastUser;
  const aiText = state.liveAssistant || lastAI;

  const switchLanguage = () => {
    const next: Lang = language === "ml" ? "en" : "ml";
    dispatch(setLanguage(next));
    if (setupOpen || pickerOpen) return; // the sheet re-renders in the new language; nothing to restart
    // Restart so the model picks up the new default language.
    stop("language");
    startedRef.current = true;
    setConnectedAt(null);
    setElapsed(0);
    void start(next, telecallerOptsRef.current ?? undefined);
  };

  const retry = () => {
    startedRef.current = true;
    setConnectedAt(null);
    setElapsed(0);
    if (isTelecallerRequest) {
      // A second call to the same lead: pick the objective again.
      setRefusal(null);
      setSetupOpen(true);
      return;
    }
    void start(language);
  };

  const close = () => dispatch(closeVoiceCall());

  const submitTyped = () => {
    if (!draft.trim()) return;
    sendText(draft);
    setDraft("");
  };

  const statusText =
    status === "error" ? state.error ?? COPY[language].error : COPY[language][status];

  return (
    <div className="xv-root" role="dialog" aria-modal="true" aria-label="Xale AI voice call">
      {/* Ambient ground */}
      <div className="xv-bg" />
      <div className="xv-aurora" aria-hidden><span /><span /><span /></div>
      <div className="xv-grain" aria-hidden />
      <div className="xv-vignette" aria-hidden />
      <div className="xv-edge" ref={edgeRef} aria-hidden />

      <div className="xv-layer">
        {/* ---- Header ---- */}
        <header className="xv-head">
          <div className="xv-brand">
            <span className="xv-mark"><img src={xaleLogo} alt="" /></span>
            <div style={{ minWidth: 0 }}>
              <div className="xv-title">{isTelecallerRequest ? `Xale AI · ${t.telecaller}` : "Xale AI"}</div>
              <div className="xv-sub">
                <span className={`xv-livedot${status === "error" ? " is-bad" : live ? "" : " is-off"}`} />
                {isTelecallerRequest && request.mode === "telecaller" && request.leadName ? request.leadName : t.live}
                {state.telecaller?.practice && (
                  <>
                    <span style={{ opacity: 0.45 }}>·</span>
                    <span style={{ color: "#ffd9a8" }}>{t.practice}</span>
                  </>
                )}
                <span style={{ opacity: 0.45 }}>·</span>
                {language === "ml" ? "മലയാളം" : "English"}
                {connectedAt != null && (
                  <>
                    <span style={{ opacity: 0.45 }}>·</span>
                    <span style={{ fontVariantNumeric: "tabular-nums" }}>{formatElapsed(elapsed)}</span>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="xv-tools">
            <div className="xv-pill" style={{ padding: 3, gap: 2, cursor: "default" }} role="group" aria-label="Voice mode">
              <button
                className={`xv-pill${!isTelecallerRequest ? " is-on" : ""}`}
                onClick={() => switchMode("assistant")}
                title={language === "ml" ? "CRM-നെപ്പറ്റി Xale AI-യോട് സംസാരിക്കുക" : "Talk to Xale AI about the CRM"}
                style={{ height: 26, padding: "0 10px", border: "none", background: !isTelecallerRequest ? undefined : "transparent" }}
              >
                {t.modeAssistant}
              </button>
              <button
                className={`xv-pill${isTelecallerRequest ? " is-on" : ""}`}
                onClick={() => switchMode("telecaller")}
                title={language === "ml" ? "AI ഒരു telecaller ആയി ഒരു ലീഡിനെ വിളിക്കുന്നു" : "Xale AI phones a lead as your telecaller"}
                style={{ height: 26, padding: "0 10px", border: "none", background: isTelecallerRequest ? undefined : "transparent" }}
              >
                <PhoneIcon /> {t.modeTelecaller}
              </button>
            </div>
            {isTelecallerRequest && state.telecaller && (
              <div className="xv-pill" style={{ padding: 3, gap: 2, cursor: "default" }} role="group" aria-label={t.tempo} title={t.tempoHint}>
                <button
                  className="xv-pill"
                  onClick={() => setTempo(state.tempo - TEMPO_STEP)}
                  disabled={state.tempo <= TEMPO_MIN}
                  aria-label={`${t.tempo} −`}
                  style={{ height: 26, width: 26, padding: 0, border: "none", background: "transparent", justifyContent: "center", opacity: state.tempo <= TEMPO_MIN ? 0.4 : 1 }}
                >
                  −
                </button>
                <span style={{ fontVariantNumeric: "tabular-nums", minWidth: 42, textAlign: "center", fontSize: 12 }}>{formatTempo(state.tempo)}</span>
                <button
                  className="xv-pill"
                  onClick={() => setTempo(state.tempo + TEMPO_STEP)}
                  disabled={state.tempo >= TEMPO_MAX}
                  aria-label={`${t.tempo} +`}
                  style={{ height: 26, width: 26, padding: 0, border: "none", background: "transparent", justifyContent: "center", opacity: state.tempo >= TEMPO_MAX ? 0.4 : 1 }}
                >
                  +
                </button>
              </div>
            )}
            {state.transcript.length > 0 && (
              <button
                className={`xv-pill${showTranscript ? " is-on" : ""}`}
                onClick={() => setShowTranscript((v) => !v)}
                title={t.transcript}
              >
                <TranscriptIcon />
                <span className="xv-count">{state.transcript.length}</span>
              </button>
            )}
            <button
              className="xv-pill"
              onClick={switchLanguage}
              title={language === "ml" ? "Switch to English" : "മലയാളത്തിലേക്ക് മാറ്റുക"}
            >
              <GlobeIcon />
              {language === "ml" ? "മലയാളം" : "English"}
            </button>
            <button className="xv-pill is-icon" onClick={close} aria-label={t.close} title={t.close}>
              <CloseIcon />
            </button>
          </div>
        </header>

        {/* ---- Telecaller: pick the lead (from the header switch / panel button) ---- */}
        {pickerOpen && request.mode === "telecaller" && (
          <main className="xv-stage" style={{ justifyContent: "center" }}>
            <LeadPicker language={language} onPick={pickLead} onCancel={close} />
          </main>
        )}

        {/* ---- Telecaller setup sheet (before the line opens) ---- */}
        {setupOpen && !pickerOpen && request.mode === "telecaller" && (
          <main className="xv-stage" style={{ justifyContent: "center" }}>
            <TelecallerSetup
              leadName={request.leadName ?? ""}
              language={language}
              initialObjective={(request.objective as never) ?? undefined}
              refusal={refusal}
              onStart={startTelecaller}
              onCancel={() => setPickerOpen(true)}
            />
          </main>
        )}

        {/* ---- Stage ---- */}
        {!setupOpen && !pickerOpen && (
        <main className="xv-stage">
          <div className="xv-orbwrap" style={{ width: orbSize, height: orbSize, margin: `${-Math.round((orbSize * ORB_BLEED) / 2)}px 0` }}>
            <SiriOrb size={orbSize} status={status} muted={state.muted} levels={levels} onEnergy={handleEnergy} />
          </div>

          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
            <div className="xv-status" key={status} aria-live="polite">
              <StatusGlyph status={status} />
              <span>{statusText}</span>
              {status === "ended" && state.endedReason && state.endedReason !== "closed" && (
                <span style={{ opacity: 0.55 }}>· {state.endedReason}</span>
              )}
            </div>
            {state.muted && live && <div className="xv-tool">{t.micOff}</div>}
            {state.toolActivity && <div className="xv-tool">{state.toolActivity}</div>}
            {(status === "error" || status === "ended") && (
              <button className="xv-pill is-primary" onClick={retry} style={{ height: 36, marginTop: 4 }}>
                <PhoneIcon /> {isTelecallerRequest ? t.callAnother : t.callAgain}
              </button>
            )}
          </div>

          {/* ---- Captions ---- */}
          <div className="xv-caps">
            {youText ? <div className="xv-cap-you">{youText}</div> : null}
            {aiText ? (
              <div className="xv-cap-ai">
                {aiText.split(/(\s+)/).map((chunk, i) =>
                  /^\s+$/.test(chunk) ? (
                    chunk
                  ) : (
                    <span className="xv-word" key={i}>
                      {chunk}
                    </span>
                  ),
                )}
              </div>
            ) : !youText && live ? (
              <div className="xv-hint">{isTelecallerRequest ? (state.telecaller?.practice ? t.practiceOpening : t.telecallerOpening) : t.opening}</div>
            ) : null}
          </div>
        </main>
        )}

        {/* ---- Telecaller: who is on the line / what the AI wrote ---- */}
        {!setupOpen && !pickerOpen && state.outcome && (
          <div style={{ padding: "0 24px 10px" }}>
            <OutcomeCard outcome={state.outcome} language={language} />
          </div>
        )}
        {!setupOpen && !pickerOpen && state.telecaller && !state.outcome && (
          <div style={{ padding: "0 24px 10px" }}>
            <TelecallerCard hello={state.telecaller} language={language} />
          </div>
        )}

        {/* ---- Chart (run_report chart / render_chart) ---- */}
        {state.chart && (
          <div style={{ padding: "0 24px 10px" }}>
            <ChartCard chart={state.chart} language={language} onDismiss={dismissChart} />
          </div>
        )}

        {/* ---- Recording ---- */}
        {state.media && (
          <div style={{ padding: "0 24px" }}>
            <RecordingCard
              key={state.media.seq}
              media={state.media}
              language={language}
              registerMediaElement={registerMediaElement}
              onMediaPlay={onMediaPlay}
              onMediaPause={onMediaPause}
              onMediaEnded={onMediaEnded}
              onDismiss={dismissMedia}
            />
          </div>
        )}

        {/* ---- Typed input ---- */}
        {typing && (
          <div className="xv-type">
            <input
              autoFocus
              className="xv-input"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") submitTyped();
              }}
              placeholder={isTelecallerRequest ? t.whisperPlaceholder : t.placeholder}
              disabled={!canTalk}
            />
            <button className="xv-pill is-primary" onClick={submitTyped} disabled={!canTalk || !draft.trim()} style={{ height: 44, padding: "0 20px" }}>
              {t.send}
            </button>
          </div>
        )}

        {/* ---- Controls ---- */}
        {!setupOpen && !pickerOpen && (
        <footer className="xv-dock">
          <button
            className={`xv-ctl${state.muted ? " is-on" : ""}`}
            onClick={toggleMute}
            disabled={!canTalk}
            aria-label={state.muted ? t.unmute : t.mute}
            title={state.muted ? t.unmute : t.mute}
          >
            <MicIcon muted={state.muted} />
          </button>
          <button
            className={`xv-ctl${typing ? " is-on" : ""}`}
            onClick={() => setTyping((v) => !v)}
            disabled={!canTalk}
            aria-label={t.type}
            title={t.type}
          >
            <KeyboardIcon />
          </button>
          <button className="xv-ctl is-danger" onClick={close} aria-label={t.end} title={t.end}>
            <EndIcon />
          </button>
        </footer>
        )}
      </div>

      {/* ---- Transcript sheet ---- */}
      {showTranscript && (
        <div className="xv-sheet-wrap">
          <button className="xv-scrim" aria-label={t.close} onClick={() => setShowTranscript(false)} />
          <section className="xv-sheet">
            <div className="xv-grip" />
            <div className="xv-sheet-head">
              <span>{t.transcript}</span>
              <button className="xv-pill is-icon" onClick={() => setShowTranscript(false)} aria-label={t.close}>
                <CloseIcon />
              </button>
            </div>
            <div className="xv-sheet-body" ref={bodyRef}>
              {state.transcript.map((line) => (
                <div
                  key={line.id}
                  className={`xv-line ${line.role === "user" ? "is-you" : "is-ai"}${line.interrupted ? " is-cut" : ""}`}
                >
                  <span className="xv-role">
                    {line.role === "user"
                      ? isTelecallerRequest
                        ? line.typed
                          ? t.whisper
                          : request.mode === "telecaller"
                            ? request.leadName
                            : t.lead
                        : t.you
                      : isTelecallerRequest && state.telecaller
                        ? state.telecaller.agentName
                        : t.ai}
                  </span>
                  <div className="xv-bubble">
                    {line.text}
                    {line.interrupted ? " …" : ""}
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>
      )}
    </div>
  );
}

function formatElapsed(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function StatusGlyph({ status }: { status: CallStatus }) {
  if (status === "listening") {
    return (
      <span className="xv-eq" aria-hidden>
        <i /><i /><i /><i />
      </span>
    );
  }
  if (status === "thinking" || status === "connecting") {
    return (
      <span className="xv-thinking" aria-hidden>
        <i /><i /><i />
      </span>
    );
  }
  if (status === "speaking") {
    return (
      <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" aria-hidden>
        <path d="M4 9v6h4l5 4V5L8 9z" fill="currentColor" stroke="none" />
        <path d="M17.5 8.5a5 5 0 0 1 0 7" />
      </svg>
    );
  }
  if (status === "error") {
    return (
      <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" aria-hidden>
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v6M12 16.5v.01" />
      </svg>
    );
  }
  return null;
}

function MicIcon({ muted }: { muted: boolean }) {
  return (
    <svg width={23} height={23} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="9" y="2.5" width="6" height="11.5" rx="3" />
      <path d="M5.2 10.5a6.8 6.8 0 0 0 13.6 0" />
      <path d="M12 17.3V21M8.5 21h7" />
      {muted && <path d="M4 3.6l16 16.8" />}
    </svg>
  );
}

function KeyboardIcon() {
  return (
    <svg width={23} height={23} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="2.5" y="6" width="19" height="12" rx="2.6" />
      <path d="M6.5 10h.01M10 10h.01M13.5 10h.01M17 10h.01M8 13.6h8" />
    </svg>
  );
}

function EndIcon() {
  return (
    <svg width={27} height={27} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path
        d="M21.7 15.4l-3.3-1.4a1.6 1.6 0 0 0-1.8.4l-1.4 1.5a13 13 0 0 1-5.1-5.1l1.5-1.4a1.6 1.6 0 0 0 .4-1.8L10.6 4.3A1.6 1.6 0 0 0 8.8 3.4L5.4 4.3A1.6 1.6 0 0 0 4.2 5.9C4.6 13.4 10.6 19.4 18.1 19.8a1.6 1.6 0 0 0 1.6-1.2l.9-3.4a1.6 1.6 0 0 0-.9-1.8z"
        transform="rotate(135 12 12)"
      />
    </svg>
  );
}

function PhoneIcon() {
  return (
    <svg width={14} height={14} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M21.7 15.4l-3.3-1.4a1.6 1.6 0 0 0-1.8.4l-1.4 1.5a13 13 0 0 1-5.1-5.1l1.5-1.4a1.6 1.6 0 0 0 .4-1.8L10.6 4.3A1.6 1.6 0 0 0 8.8 3.4L5.4 4.3A1.6 1.6 0 0 0 4.2 5.9C4.6 13.4 10.6 19.4 18.1 19.8a1.6 1.6 0 0 0 1.6-1.2l.9-3.4a1.6 1.6 0 0 0-.9-1.8z" />
    </svg>
  );
}

function GlobeIcon() {
  return (
    <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} aria-hidden>
      <circle cx="12" cy="12" r="9" />
      <path d="M3.2 9h17.6M3.2 15h17.6M12 3a15 15 0 0 1 0 18M12 3a15 15 0 0 0 0 18" />
    </svg>
  );
}

function TranscriptIcon() {
  return (
    <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.9} strokeLinecap="round" aria-hidden>
      <path d="M4 6.5h16M4 11h11M4 15.5h13M4 20h7" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" aria-hidden>
      <path d="M6 6l12 12M18 6L6 18" />
    </svg>
  );
}
